import {
  MediaQuestionConfig,
  TextQuestionConfig,
  WorkflowPageConfig,
  WorkflowQuestionConfig,
  WorkflowQuestionType,
  newGUID,
} from '@gripp/shared-logic';

/**
 * IDP (Intelligent Document Processing) question rules for the routine editor —
 * plan 0027, Phase 2.
 *
 * The IDP source question is a **config invariant, not a form field**: extraction
 * reads exactly one image question, and the API's enqueue planner
 * (`proxies/routineIdpEnqueue.ts`) skips the whole job — warning only, submission
 * still succeeds — when it finds two source questions carrying media. An admin
 * would see a routine that quietly stopped extracting. The editor is what keeps
 * that state from existing, so the decision is kept here as pure functions that
 * can be tested without rendering the editor.
 */

/**
 * Where the source question is pinned. It lives on the first page because
 * questions cannot be moved between pages in the editor, so "question #1" has to
 * mean the first question of the first page.
 */
export const IDP_SOURCE_PAGE_INDEX = 0;
export const IDP_SOURCE_QUESTION_INDEX = 0;

/**
 * The document question extraction reads from. Both halves matter: the API
 * planner requires `type === MEDIA` as well as the flag, so a flag left behind on
 * a question whose type was changed is inert rather than a second source.
 */
export const isIdpSourceQuestion = (
  question: WorkflowQuestionConfig | undefined
): boolean =>
  question?.type === WorkflowQuestionType.Media &&
  (question as MediaQuestionConfig).isIdpSource === true;

/**
 * A field the extraction fills in. Gated to Text in v1 (PRD "v1 restricts IDP
 * management to Text question types only"), matching the API planner's own
 * `type === TEXT` requirement.
 */
export const isIdpManagedQuestion = (
  question: WorkflowQuestionConfig | undefined
): boolean =>
  question?.type === WorkflowQuestionType.Text &&
  (question as TextQuestionConfig).idpManaged === true;

/**
 * Front-door mirror of the API enqueue planner's own `MAX_IDP_FIELDS` limit
 * (Azure Structured Outputs schema hard cap). Kept as the editor's cap too so
 * an admin hits a clear wall here instead of a silent server-side rejection.
 */
export const MAX_IDP_MANAGED_FIELDS = 100;

/** Total managed-field count across the whole routine, for the cap check. */
export const countIdpManagedQuestions = (
  pages: (WorkflowPageConfig | undefined)[] | undefined
): number =>
  (pages ?? []).reduce(
    (total, page) =>
      total + (page?.questions ?? []).filter(isIdpManagedQuestion).length,
    0
  );

/** Where a source question was found, across the whole routine. */
export interface IdpSourcePosition {
  pageIndex: number;
  questionIndex: number;
}

/** What the editor must do to restore "exactly one pinned source question". */
export interface IdpSourceInvariantPlan {
  /** Extra source questions, which must have `isIdpSource` cleared. */
  demote: IdpSourcePosition[];
  /**
   * First-page index of the surviving source when it is not pinned first yet.
   * `undefined` when it is already in place, or when there is nothing to move.
   */
  moveToPinnedFrom?: number;
  /** No source survives — one must be inserted at the pinned slot. */
  insert: boolean;
}

/**
 * Decides how to reach exactly one source question, pinned first on the first
 * page.
 *
 * The survivor is the first source on the first page. A source found only on a
 * later page is demoted and replaced rather than moved: the editor's field arrays
 * are per page, so there is no move across that boundary. That state is not
 * reachable through the UI (nothing but this module creates a source question,
 * and duplicating one clears the flag) — it exists so a hand-edited or
 * pre-invariant config normalises itself on open instead of silently extracting
 * nothing.
 */
export const planIdpSourceInvariant = (
  pages: (WorkflowPageConfig | undefined)[] | undefined
): IdpSourceInvariantPlan => {
  const sources: IdpSourcePosition[] = [];
  (pages ?? []).forEach((page, pageIndex) => {
    (page?.questions ?? []).forEach((question, questionIndex) => {
      if (isIdpSourceQuestion(question)) {
        sources.push({ pageIndex, questionIndex });
      }
    });
  });

  const survivor = sources.find(
    (source) => source.pageIndex === IDP_SOURCE_PAGE_INDEX
  );

  return {
    demote: sources.filter((source) => source !== survivor),
    moveToPinnedFrom:
      survivor && survivor.questionIndex !== IDP_SOURCE_QUESTION_INDEX
        ? survivor.questionIndex
        : undefined,
    insert: !survivor,
  };
};

/**
 * The pinned document question, built to the same shape as an admin-added
 * question so the editor's existing renumbering (`order`/`name` follow the
 * question's position) applies to it unchanged.
 *
 * `instructions` is seeded rather than left blank because `WorkflowAdminSchema`
 * requires instructions on every question — an auto-inserted question with none
 * would block save with no visible cause. The admin can edit the text.
 */
export const buildIdpSourceQuestion = (
  instructions: string
): MediaQuestionConfig => ({
  id: newGUID(),
  name: `Question ${IDP_SOURCE_QUESTION_INDEX + 1}`,
  order: IDP_SOURCE_QUESTION_INDEX + 1,
  type: WorkflowQuestionType.Media,
  required: true,
  instructions,
  isIdpSource: true,
});
